/**
 * Global-surgical-period-aware billing — the generic date-arithmetic/modifier-selection mechanism
 * behind both `orthopedics/globalSurgicalPeriod.ts` (`BACKLOG.md` M7) and
 * `ophthalmology/cataractGlobalPeriod.ts` (`BACKLOG.md` M10). Extracted here, out of the orthopedics
 * module it originated in, once a second real consumer needed it — matching this repo's own
 * "favor the general, reusable solution once a pattern actually recurs" convention rather than one
 * module depending on another module's internals, or a third copy being written for M11 (OB-GYN,
 * antepartum-visit-counter billing) when that lands.
 *
 * Each specialty module still owns its own CPT-to-global-period-days catalog — that part is
 * genuinely specialty-specific, unlike this window/modifier logic, which is pure CMS billing-rule
 * arithmetic with no domain content of its own. `orthopedics/globalSurgicalPeriod.ts` re-exports
 * everything below for backward compatibility with its own existing imports.
 *
 * The window/modifier logic itself is ordinary, fully-specified date arithmetic and a deterministic
 * mapping from (window position, relationship) to modifier — it needs no CMS lookup to test.
 * The modifier *definitions* it encodes (24/25/58/78/79) are standard, long-stable CMS/CPT modifier
 * meanings, carried over from general knowledge rather than checked against the current CMS Global
 * Surgery Booklet — what is safe to trust is that this logic correctly implements the rules as stated
 * in the comments below; the tests prove that, not that the rules themselves are current CMS policy.
 */

/** The only three global-period lengths CMS uses: 0-day (minor procedure, endoscopy), 10-day (minor
 * procedure), 90-day (major surgery). `XXX` (not applicable) and `YYY`/`ZZZ` (carrier-priced /
 * follow-up to another procedure) exist in the real CMS table but are out of scope for a demo
 * catalog — every code any consumer's own catalog carries is one of these three numeric periods. */
export const GLOBAL_PERIOD_DAYS_VALUES = [0, 10, 90] as const;
export type GlobalPeriodDays = (typeof GLOBAL_PERIOD_DAYS_VALUES)[number];

export function isGlobalPeriodDays(value: number): value is GlobalPeriodDays {
  return (GLOBAL_PERIOD_DAYS_VALUES as readonly number[]).includes(value);
}

// --- Window arithmetic ----------------------------------------------------------------------------

/** `surgeryDate`/`endDate` are calendar dates (`YYYY-MM-DD`), matching `columns.ts`'s `calendarDate`
 * convention — never parsed as an instant, so this never drifts by a day across timezones the way
 * `columns.ts`'s own header warns a `date` value would if handled as one. */
export interface GlobalPeriodWindow {
  readonly surgeryDate: string;
  readonly globalPeriodDays: GlobalPeriodDays;
  /** `surgeryDate + globalPeriodDays`, inclusive — the last day still inside the postoperative
   * period. CMS counts the day of surgery as day 0 of a 90/10-day period. */
  readonly endDate: string;
}

function addDaysToCalendarDate(date: string, days: number): string {
  const parts = date.split('-').map(Number);
  const [year, month, day] = parts;
  if (year === undefined || month === undefined || day === undefined) {
    // Every real caller validates `date` against `calendarDateSchema` first — this is a fail-closed
    // backstop for a malformed value reaching this function some other way, not an expected path.
    throw new Error(`not a calendar date (YYYY-MM-DD): ${date}`);
  }
  // UTC, deliberately: a calendar date has no timezone of its own (`columns.ts`'s own reasoning), so
  // arithmetic on it must not be sensitive to the host's local timezone either.
  const asDate = new Date(Date.UTC(year, month - 1, day));
  asDate.setUTCDate(asDate.getUTCDate() + days);
  const y = asDate.getUTCFullYear();
  const m = String(asDate.getUTCMonth() + 1).padStart(2, '0');
  const d = String(asDate.getUTCDate()).padStart(2, '0');
  return `${String(y)}-${m}-${d}`;
}

export function computeGlobalPeriodWindow(
  surgeryDate: string,
  globalPeriodDays: GlobalPeriodDays,
): GlobalPeriodWindow {
  return {
    surgeryDate,
    globalPeriodDays,
    endDate: addDaysToCalendarDate(surgeryDate, globalPeriodDays),
  };
}

/** Whether `serviceDate` falls on or after the day of surgery and on or before the window's end —
 * the day of surgery itself counts as inside the window. */
export function isWithinGlobalPeriod(serviceDate: string, window: GlobalPeriodWindow): boolean {
  return serviceDate >= window.surgeryDate && serviceDate <= window.endDate;
}

// --- Modifier selection ----------------------------------------------------------------------------

/**
 * Why a claim is being billed on or after the day of surgery — the fact a coder actually knows and
 * the input this function needs to pick the right modifier. Five relationships, matching CMS/CPT
 * modifiers 24/25/58/78/79:
 *
 *   - `significant_separately_identifiable_em_same_day` → **25**: an E/M service on the *same day* as
 *     a minor procedure, significant and separately identifiable from it.
 *   - `unrelated_em` → **24**: an E/M service, unrelated to the surgery, during the postop period.
 *   - `staged_or_related_procedure` → **58**: a planned/staged or more extensive related procedure
 *     during the postop period.
 *   - `unplanned_return_related_complication` → **78**: an unplanned return to the OR for a
 *     complication of the original procedure, during its postop period.
 *   - `unrelated_procedure_or_service` → **79**: an unrelated procedure by the same physician during
 *     the postop period.
 */
export const GLOBAL_PERIOD_RELATIONSHIPS = [
  'significant_separately_identifiable_em_same_day',
  'unrelated_em',
  'staged_or_related_procedure',
  'unplanned_return_related_complication',
  'unrelated_procedure_or_service',
] as const;
export type GlobalPeriodRelationship = (typeof GLOBAL_PERIOD_RELATIONSHIPS)[number];

export function isGlobalPeriodRelationship(value: string): value is GlobalPeriodRelationship {
  return (GLOBAL_PERIOD_RELATIONSHIPS as readonly string[]).includes(value);
}

const GLOBAL_PERIOD_RELATIONSHIP_LABELS: Readonly<Record<GlobalPeriodRelationship, string>> = {
  significant_separately_identifiable_em_same_day:
    'Significant, separately identifiable E/M — same day as the procedure',
  unrelated_em: 'Unrelated E/M service during the postop period',
  staged_or_related_procedure: 'Staged or more extensive related procedure',
  unplanned_return_related_complication: 'Unplanned return to the OR for a complication',
  unrelated_procedure_or_service: 'Unrelated procedure or service during the postop period',
};

/** English-only domain vocabulary — see `orthopedics/conditionTemplates.ts`'s `conditionTypeLabel` for
 * the shared-function-not-locale-key precedent this follows. */
export function globalPeriodRelationshipLabel(relationship: GlobalPeriodRelationship): string {
  return GLOBAL_PERIOD_RELATIONSHIP_LABELS[relationship];
}

export const GLOBAL_PERIOD_MODIFIERS = ['24', '25', '58', '78', '79'] as const;
export type GlobalPeriodModifier = (typeof GLOBAL_PERIOD_MODIFIERS)[number];

export type GlobalPeriodModifierResult =
  | {
      readonly ok: true;
      /** `null` when no modifier is needed at all — the service falls outside the global period
       * (ordinary billing applies), or the procedure carries no global period (`globalPeriodDays`
       * 0). */
      readonly modifier: GlobalPeriodModifier | null;
      readonly reason:
        | 'no_global_period'
        | 'outside_global_period'
        | 'same_day_significant_em'
        | 'unrelated_em_in_postop_period'
        | 'staged_or_related_procedure'
        | 'unplanned_return_for_complication'
        | 'unrelated_procedure_in_postop_period';
    }
  | { readonly ok: false; readonly reason: 'invalid_service_date' };

/**
 * The modifier (or explicit "none needed") for one claim, given the global-period window it falls
 * against and why it is being billed. Pure — no clock, no database; `serviceDate`/`surgeryDate` are
 * both caller-supplied calendar-date strings, compared lexicographically (safe for `YYYY-MM-DD`).
 *
 * `relationship` is never inferred from the dates alone — a coder's stated reason is a fact this
 * function takes as input, not something it guesses at from timing. What this function *does* enforce
 * is that the stated reason is consistent with the dates: `significant_separately_identifiable_em_
 * same_day` only makes sense exactly on the day of surgery, and the four in-postop relationships only
 * make sense on a day *after* surgery, never on the day of surgery itself (that day belongs to the
 * original procedure, not a modifier scenario) or before it. A mismatch returns `ok: false` rather
 * than silently picking a modifier that does not match the claimed relationship.
 */
export function determineGlobalPeriodModifier(input: {
  readonly serviceDate: string;
  readonly surgeryDate: string;
  readonly globalPeriodDays: GlobalPeriodDays;
  readonly relationship: GlobalPeriodRelationship;
}): GlobalPeriodModifierResult {
  const { serviceDate, surgeryDate, globalPeriodDays, relationship } = input;

  if (relationship === 'significant_separately_identifiable_em_same_day') {
    if (serviceDate !== surgeryDate) return { ok: false, reason: 'invalid_service_date' };
    return { ok: true, modifier: '25', reason: 'same_day_significant_em' };
  }

  // Every other relationship describes something happening strictly after the day of surgery.
  if (serviceDate <= surgeryDate) return { ok: false, reason: 'invalid_service_date' };

  if (globalPeriodDays === 0) {
    return { ok: true, modifier: null, reason: 'no_global_period' };
  }

  const window = computeGlobalPeriodWindow(surgeryDate, globalPeriodDays);
  if (!isWithinGlobalPeriod(serviceDate, window)) {
    return { ok: true, modifier: null, reason: 'outside_global_period' };
  }

  switch (relationship) {
    case 'unrelated_em':
      return { ok: true, modifier: '24', reason: 'unrelated_em_in_postop_period' };
    case 'staged_or_related_procedure':
      return { ok: true, modifier: '58', reason: 'staged_or_related_procedure' };
    case 'unplanned_return_related_complication':
      return { ok: true, modifier: '78', reason: 'unplanned_return_for_complication' };
    case 'unrelated_procedure_or_service':
      return { ok: true, modifier: '79', reason: 'unrelated_procedure_in_postop_period' };
    default:
      // Exhaustive over GLOBAL_PERIOD_RELATIONSHIPS minus the same-day case handled above —
      // unreachable, but a thrown error beats a silent fallthrough if that ever stops being true.
      throw new Error(`unreachable global-period relationship: ${String(relationship)}`);
  }
}
